import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function BusinessTaxFormsGuidePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            <button onClick={() => navigate('/blog')} className="inline-flex items-center cursor-pointer gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </button>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="pt-32 pb-16">
        <header className="px-6 lg:px-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full">Business Tax</span>
              <span className="text-gray-500">•</span>
              <time className="text-gray-600">January 10, 2025</time>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">10 min read</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Business Tax Forms Explained: 1065, 1120, and 1120-S
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Navigate the complexities of business tax returns. Discover how different entity structures require different forms and how automation simplifies compliance.
            </p>
          </div>
        </header>

        {/* Featured Image */}
        <div className="px-6 lg:px-8 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/ba274dee2a0da6de2716e41545dacdfc.jpg"
                alt="Business Tax Forms Guide"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Business Entity Tax Forms</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                One of the most critical decisions business owners make is choosing their entity structure—and each structure comes with its own unique tax form and compliance requirements. Whether you're advising a new startup or managing an established corporation, understanding the nuances of Forms 1065, 1120, and 1120-S is essential for providing comprehensive tax services.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                These forms aren't just about reporting income—they reflect fundamentally different approaches to taxation, ownership structures, and compliance obligations. Let's explore each form in detail and understand when and how to use them effectively.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Form 1065: U.S. Return of Partnership Income</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Who Files Form 1065?</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Form 1065 is filed by partnerships and multi-member LLCs that elect to be taxed as partnerships. This includes general partnerships, limited partnerships (LPs), limited liability partnerships (LLPs), and multi-member limited liability companies (LLCs) that haven't elected corporate taxation.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Key Features of Partnership Taxation</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Partnerships are pass-through entities, meaning the partnership itself doesn't pay federal income tax. Instead, income, deductions, credits, and other tax items flow through to the partners, who report their share on their individual tax returns. This pass-through treatment avoids the double taxation that affects C corporations.
              </p>

              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Schedule K-1: The Critical Component</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Each partner receives a Schedule K-1 (Form 1065) showing their distributive share of partnership income, deductions, and credits. The K-1 is one of the most complex tax documents, requiring careful attention to:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Ordinary business income or loss</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Rental real estate income or loss</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Guaranteed payments to partners</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Capital gains and losses</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Section 179 deduction allocations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Self-employment income</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Common Challenges with Form 1065</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Partnership returns are notoriously complex. Common challenges include:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Basis calculations:</strong> Partners must track their basis in the partnership to determine deductibility of losses and gain on disposition</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Capital account reconciliation:</strong> Maintaining accurate capital accounts using tax basis, GAAP, or Section 704(b) methods</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Special allocations:</strong> Ensuring allocations have substantial economic effect under Section 704(b)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Self-employment tax:</strong> Determining which partners are subject to self-employment tax on their distributive share</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Form 1120: U.S. Corporation Income Tax Return</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Who Files Form 1120?</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Form 1120 is filed by C corporations—entities that are taxed separately from their owners. This includes traditional corporations that haven't elected S corporation status and LLCs that have elected to be taxed as C corporations.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Understanding C Corporation Taxation</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Unlike pass-through entities, C corporations pay tax at the entity level on their taxable income. The current federal corporate tax rate is 21% following the Tax Cuts and Jobs Act. When profits are distributed to shareholders as dividends, those dividends are taxed again at the shareholder level—the famous "double taxation" of C corporations.
              </p>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">When C Corporation Status Makes Sense</h4>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Despite double taxation, C corporation status can be advantageous when:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Seeking venture capital or planning an IPO</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Retaining earnings for business growth rather than distributing profits</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Offering employee stock options and equity compensation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700">Benefiting from the 21% flat corporate rate when individual rates would be higher</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Key Components of Form 1120</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Form 1120 includes detailed schedules covering income, deductions, tax computation, and balance sheet information. Critical areas include:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Schedule M-1:</strong> Reconciliation of income per books with income per return</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Schedule M-2:</strong> Analysis of unappropriated retained earnings</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Schedule M-3:</strong> Required for corporations with total assets of $10 million or more, providing detailed book-tax differences</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Schedule UTP:</strong> Uncertain tax position statement for corporations with assets over $10 million</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Form 1120-S: U.S. Income Tax Return for an S Corporation</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Who Files Form 1120-S?</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Form 1120-S is filed by S corporations—corporations that have elected pass-through taxation under Subchapter S of the Internal Revenue Code. To qualify for S corporation status, a corporation must meet specific requirements including having no more than 100 shareholders, only one class of stock, and only eligible shareholders (generally U.S. citizens and residents).
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">The Best of Both Worlds?</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                S corporations combine the liability protection of a corporation with the pass-through taxation of a partnership. Income, deductions, and credits flow through to shareholders via Schedule K-1 (Form 1120-S), avoiding double taxation while maintaining corporate formalities.
              </p>

              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">S Corporation Advantages</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Self-employment tax savings:</strong> Only reasonable compensation to shareholder-employees is subject to payroll taxes; distributions are not</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Pass-through taxation:</strong> Avoids double taxation while maintaining corporate structure</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>QBI deduction:</strong> Shareholders may qualify for the 20% qualified business income deduction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Basis step-up:</strong> Shareholders can increase basis for their share of corporate debt in certain situations</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Critical Compliance Issues for S Corporations</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                S corporations face unique compliance challenges that can result in loss of S election if not properly managed:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Reasonable compensation:</strong> The IRS scrutinizes shareholder-employee compensation to ensure it's reasonable, not artificially low to avoid payroll taxes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>One class of stock:</strong> Maintaining only one class of stock; disproportionate distributions can create a second class</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Shareholder basis:</strong> Tracking each shareholder's basis to determine loss deductibility and gain on disposition</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Built-in gains tax:</strong> C corporations converting to S status may face built-in gains tax on appreciated assets sold within five years</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Comparing the Three Forms: A Decision Framework</h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-2 border-gray-200 rounded-xl overflow-hidden">
                  <thead className="bg-emerald-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">Feature</th>
                      <th className="px-6 py-4 text-left font-semibold">Form 1065 (Partnership)</th>
                      <th className="px-6 py-4 text-left font-semibold">Form 1120 (C Corp)</th>
                      <th className="px-6 py-4 text-left font-semibold">Form 1120-S (S Corp)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">Taxation</td>
                      <td className="px-6 py-4 text-gray-700">Pass-through</td>
                      <td className="px-6 py-4 text-gray-700">Entity-level (21%)</td>
                      <td className="px-6 py-4 text-gray-700">Pass-through</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Double Taxation</td>
                      <td className="px-6 py-4 text-gray-700">No</td>
                      <td className="px-6 py-4 text-gray-700">Yes</td>
                      <td className="px-6 py-4 text-gray-700">No</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">Owner Limits</td>
                      <td className="px-6 py-4 text-gray-700">Unlimited</td>
                      <td className="px-6 py-4 text-gray-700">Unlimited</td>
                      <td className="px-6 py-4 text-gray-700">Max 100</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Self-Employment Tax</td>
                      <td className="px-6 py-4 text-gray-700">On distributive share</td>
                      <td className="px-6 py-4 text-gray-700">N/A</td>
                      <td className="px-6 py-4 text-gray-700">Only on wages</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="px-6 py-4 font-semibold text-gray-900">Flexibility</td>
                      <td className="px-6 py-4 text-gray-700">High (special allocations)</td>
                      <td className="px-6 py-4 text-gray-700">High (multiple classes)</td>
                      <td className="px-6 py-4 text-gray-700">Limited (one class)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Leveraging Automation for Business Tax Returns</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Business tax returns are significantly more complex than individual returns, involving multiple schedules, detailed financial statements, and intricate calculations. AI-powered automation transforms this complexity into manageable workflows:
              </p>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Automated data import</strong> from accounting systems like QuickBooks, Xero, and other platforms</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Intelligent form selection</strong> based on entity type and election status</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Automatic K-1 generation</strong> with proper allocation of income, deductions, and credits</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Book-tax difference reconciliation</strong> for Schedule M-1 and M-3 preparation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Basis tracking</strong> for partners and S corporation shareholders</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Compliance checks</strong> for S corporation eligibility and reasonable compensation</span>
                </li>
              </ul>
            </section>

            <section className="mb-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Streamline Your Business Tax Workflow</h2>
              <p className="text-white/90 leading-relaxed mb-6">
                InstaVisible's AI-powered platform handles the complexity of Forms 1065, 1120, and 1120-S automatically. From data import to K-1 generation, our intelligent system ensures accuracy while reducing preparation time by up to 70%. Focus on strategic advisory services while we handle the technical details.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
                Request a Video Demo
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusion</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Understanding the differences between Forms 1065, 1120, and 1120-S is fundamental to providing comprehensive business tax services. Each form reflects a different approach to business taxation, with unique advantages, challenges, and compliance requirements.
              </p>
              <p className="text-gray-700 leading-relaxed">
                As business structures become more complex and tax regulations continue to evolve, leveraging AI-powered automation isn't just about efficiency—it's about maintaining accuracy, ensuring compliance, and freeing tax professionals to focus on strategic planning and client relationships. The future of business tax preparation lies in combining professional expertise with intelligent automation.
              </p>
            </section>
          </div>
        </div>

        {/* Related Articles */}
        <div className="px-6 lg:px-8 mt-16">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a href="/blog/understanding-form-1040" className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 transition-all">
                <span className="text-sm text-emerald-600 font-semibold mb-2 block">Tax Forms</span>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">Understanding Form 1040: A Complete Guide for Tax Professionals</h4>
                <p className="text-gray-600 text-sm">Master the fundamentals of Form 1040 and its schedules.</p>
              </a>
              <a href="/blog/state-tax-compliance" className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 transition-all">
                <span className="text-sm text-emerald-600 font-semibold mb-2 block">State Tax</span>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">Multi-State Tax Compliance: Navigating 50 Different Tax Systems</h4>
                <p className="text-gray-600 text-sm">Simplify multi-state tax filing with AI-powered projections.</p>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                Professional AI-powered tax solutions for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge technology.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</a></li>
                <li><a href="/ai-agents" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</a></li>
                <li><a href="/blog" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</a></li>
                <li><a href="/privacy-policy" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
